/* eslint-disable indent */
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  TextField,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { Check } from '@mui/icons-material'

import {
  createFlowConditionCheck,
  createFlowTaskInteractionCheck,
  cacheCreateFlowConditionCheck,
  cacheCreateFlowTaskInteractionCheck
} from '../../../../../../actions'
import {
  attributesNames,
  comparisonOperators,
  dialogSize,
  textFieldTypes,
  variantTypes
} from '../../../../../../constants'
import { checkErrorResponse, Logger, LoggerTypes } from '../../../../../../utils'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  iconNames,
  Button,
  buttonModes,
  buttonTypes,
  FormTitle
} from '../../../../../../components'
import '../../../common-elements-style.scss'
import './style.scss'

/**
 * Display the Interaction dialog to import in tasks either a `variable` or a `resource`
 *
 * @param {object} currentProject current Project
 * @param {object} currentScenario current Scenario
 * @param {object} currentFlowGroup current FlowGroup
 * @param {object} currentCondition  current condition
 * @param {boolean} openDialog is the dialog popin open
 * @param {func} setOpenDialog function to open/close the dialog
 * @param {Array<object>} variables scenario variables
 * @param {Array<object>} resources scenario resources
 * @param {Array<object>} parameters scenario parameters
 * @returns {Dialog}
 */
export const FlowElementConditionDialog = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  // -- Condition's checks
  currentCondition,
  // -- Or Task's checks
  currentTaskInstance,
  variables,
  resources,
  parameters,
  openDialog,
  setOpenDialog
  // -- refresh after saving
  // refreshAfterSaving
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const [accordionExpandedLeft, setAccordionExpandedLeft] = useState(`left-${attributesNames.RESOURCES}`)
  const [accordionExpandedRight, setAccordionExpandedRight] = useState(`right-${attributesNames.RESOURCES}`)

  const [left_operand_id, setLeftOperandId] = useState()
  const [operator, setOperator] = useState(comparisonOperators.EQUAL)
  const [right_operand_id, setRightOperandId] = useState()
  const [right_operand_value, setRightOperandValue] = useState('0')

  const [errors, setErrors] = useState(null)

  // --

  const onDialogClose = (e) => {
    if (e) {
      e.preventDefault()
    }
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onDialogClose()')
    setOpenDialog(false)
  }

  const onChangeAccordionLeft = (panel) => (e, newExpanded) => {
    e.preventDefault()
    setAccordionExpandedLeft(newExpanded ? panel : false)

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onChangeAccordionLeft()', { newExpanded })
  }

  const onChangeAccordionRight = (panel) => (e, newExpanded) => {
    e.preventDefault()
    setAccordionExpandedRight(newExpanded ? panel : false)

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onChangeAccordionRight()', { newExpanded })
  }

  /**
   * Save the condition object to create
   * @returns {void}
   */
  const onSave = () => {
    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null
    const cid = currentCondition?.id || null
    const tid = currentTaskInstance?.id || null

    const check = {
      left_operand_id,
      operator,
      right_operand_id,
      right_operand_value
    }

    setErrors(null)

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onSave()', { check })

    dispatch(
      tid && !cid
        ? createFlowTaskInteractionCheck(pid, sid, fid, tid, check)
        : createFlowConditionCheck(pid, sid, fid, cid, check)
    )
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onSave().dispatch(...).then()', { res })

        // -- reload the FlowGroups data.
        // if (refreshAfterSaving && typeof refreshAfterSaving === 'function') {
        //   refreshAfterSaving()
        // }
        // enqueueSnackbar(t('common.api_success'), { variant: 'success' })

        onDialogClose()
        dispatch(
          tid && !cid
            ? cacheCreateFlowTaskInteractionCheck(currentFlowGroup, tid, res.data)
            : cacheCreateFlowConditionCheck(currentFlowGroup, cid, res.data)
        )
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'FlowElementConditionDialog.onSave().dispatch(...).catch()', { res })

        checkErrorResponse(res, enqueueSnackbar, t)

        // -- set the error to the corresponding tab
        if (res?.response?.data?.fields) {
          setErrors(res.response.data.fields)
        }
      })
  }

  // -- Left selector (`resources` or `variable` or `parameter`)
  const onLeftSelect = (id) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onLeftSelect()', { id })

    setLeftOperandId(id)
  }

  const onRightSelect = async (id) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onRightSelect()', { id })

    await setRightOperandId(id)
  }

  const onRightOperandValueChange = async (e) => {
    e.preventDefault()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionDialog.onRightOperandValueChange()', {
      right_operand_value: _value
    })

    await setRightOperandValue(_value)
    await setRightOperandId(null)
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementConditionDialog.render()', {
    currentProject,
    currentScenario,
    currentFlowGroup,
    currentCondition,
    currentTaskInstance,
    openDialog,
    variables,
    resources,
    parameters,
    check: {
      left_operand_id,
      operator,
      right_operand_id,
      right_operand_value
    }
  })

  return (
    openDialog && (
      <Dialog
        fullWidth={true}
        maxWidth={dialogSize.LARGE}
        open={openDialog}
        // onClose={onDialogClose} // <-- allow to close the popin while clicking on background
        aria-labelledby="form-dialog-title"
        className={'FlowElementDialog FlowElementConditionDialog'}
      >
        <DialogContent>
          <FormTitle title={t('flowActions.conditions.dialog.title')} style={{ fontSize: '20px' }} />
          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* === COLUMN 01 === */}
            <Grid item xs={0} sm={0} md={5}>
              {/*  -- RESOURCES --  */}
              {resources && (
                <Accordion
                  expanded={accordionExpandedLeft === `left-${attributesNames.RESOURCES}`}
                  onChange={onChangeAccordionLeft(`left-${attributesNames.RESOURCES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.RESOURCES}-condition-content`}
                    id={`${attributesNames.RESOURCES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.RESOURCE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.resources')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {resources.map((resource, index) => (
                        <MenuItem
                          key={`resource-${index}`}
                          onClick={() => onLeftSelect(resource.id)}
                          className={resource.id === left_operand_id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{resource.id === left_operand_id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{resource.name}</span>{' '}
                              <span className="quantity">({resource.quantity})</span>
                            </div>
                            <div className="description">{resource.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}
              {/*  -- VARIABLES --  */}
              {variables && (
                <Accordion
                  expanded={accordionExpandedLeft === `left-${attributesNames.VARIABLES}`}
                  onChange={onChangeAccordionLeft(`left-${attributesNames.VARIABLES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.VARIABLES}-condition-content`}
                    id={`${attributesNames.VARIABLES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.VARIABLE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.variables')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {variables.map((variable, index) => (
                        <MenuItem
                          key={`variable-${index}`}
                          onClick={() => onLeftSelect(variable.id)}
                          className={variable.id === left_operand_id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{variable.id === left_operand_id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{variable.name}</span>{' '}
                              <span className="quantity">
                                ({variable.initial_value} {variable.unit})
                              </span>
                            </div>
                            <div className="description">{variable.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}
              {/*  -- PARAMETERS --  */}
              {parameters && (
                <Accordion
                  expanded={accordionExpandedLeft === `left-${attributesNames.PARAMETERS}`}
                  onChange={onChangeAccordionLeft(`left-${attributesNames.PARAMETERS}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.PARAMETERS}-condition-content`}
                    id={`${attributesNames.PARAMETERS}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.PARAMETER} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.parameters')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {parameters.map((parameter, index) => (
                        <MenuItem
                          key={`parameter-${index}`}
                          onClick={() => onLeftSelect(parameter.id)}
                          className={parameter.id === left_operand_id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{parameter.id === left_operand_id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{parameter.name}</span>{' '}
                              <span className="quantity">
                                ({parameter.value} {parameter.unit})
                              </span>
                            </div>
                            <div className="description">{parameter.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}
              <FormControl error={Boolean(errors && errors?.left_operand_id)}>
                <FormHelperText>{(errors && errors?.left_operand_id) || ''}</FormHelperText>
              </FormControl>
            </Grid>

            {/* === COLUMN 02 === */}
            <Grid item xs={0} sm={0} md={2}>
              <Accordion expanded={true} style={{ height: '100%' }}>
                <AccordionDetails>
                  <MenuList dense>
                    {Object.values(comparisonOperators).map((_operator, index) => (
                      <MenuItem
                        key={`operator-${index}`}
                        onClick={() => setOperator(_operator)}
                        className={_operator === operator ? 'lineSelected' : ''}
                      >
                        <ListItemIcon>{_operator === operator ? <Check /> : null}</ListItemIcon>
                        <ListItemText inset className="operator">
                          {_operator}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </MenuList>
                </AccordionDetails>
                <FormControl error={Boolean(errors && errors?.operator)}>
                  <FormHelperText>{(errors && errors?.operator) || ''}</FormHelperText>
                </FormControl>
              </Accordion>
            </Grid>

            {/* === COLUMN 03 === */}
            <Grid item xs={0} sm={0} md={5}>
              {/*  -- RESOURCES --  */}
              {resources && (
                <Accordion
                  expanded={accordionExpandedRight === `right-${attributesNames.RESOURCES}`}
                  onChange={onChangeAccordionRight(`right-${attributesNames.RESOURCES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.RESOURCES}-condition-content`}
                    id={`${attributesNames.RESOURCES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.RESOURCE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.resources')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {resources.map((resource, index) => (
                        <MenuItem
                          key={`resource-${index}`}
                          onClick={() => onRightSelect(resource.id)}
                          className={resource.id === right_operand_id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{resource.id === right_operand_id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{resource.name}</span>{' '}
                              <span className="quantity">({resource.quantity})</span>
                            </div>
                            <div className="description">{resource.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*  -- VARIABLES --  */}
              {variables && (
                <Accordion
                  expanded={accordionExpandedRight === `right-${attributesNames.VARIABLES}`}
                  onChange={onChangeAccordionRight(`right-${attributesNames.VARIABLES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.VARIABLES}-condition-content`}
                    id={`${attributesNames.VARIABLES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.VARIABLE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.variables')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {variables.map((variable, index) => (
                        <MenuItem
                          key={`variable-${index}`}
                          onClick={() => onRightSelect(variable.id)}
                          className={variable.id === right_operand_id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{variable.id === right_operand_id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{variable.name}</span>{' '}
                              <span className="quantity">
                                ({variable.initial_value} {variable.unit})
                              </span>
                            </div>
                            <div className="description">{variable.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*  -- PARAMETERS --  */}
              {parameters && (
                <Accordion
                  expanded={accordionExpandedRight === `right-${attributesNames.PARAMETERS}`}
                  onChange={onChangeAccordionRight(`right-${attributesNames.PARAMETERS}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.PARAMETERS}-condition-content`}
                    id={`${attributesNames.PARAMETERS}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.PARAMETER} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.parameters')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {parameters.map((parameter, index) => (
                        <MenuItem
                          key={`parameter-${index}`}
                          onClick={() => onRightSelect(parameter.id)}
                          className={parameter.id === right_operand_id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{parameter.id === right_operand_id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{parameter.name}</span>{' '}
                              <span className="quantity">
                                ({parameter.value} {parameter.unit})
                              </span>
                            </div>
                            <div className="description">{parameter.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*  -- CUSTOM VALUE --  */}
              <Accordion
                expanded={accordionExpandedRight === 'right-custom-value'}
                onChange={onChangeAccordionRight('right-custom-value')}
              >
                <AccordionSummary aria-controls={'custom-value-condition-content'} id={'custom-value-condition-header'}>
                  <Typography>
                    <Icon name={iconNames.CUSTOM} size={14} />
                    <span className="iconAccordion">
                      {t('flowActions.conditions.dialog.accordion.custom_value.title')}
                    </span>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MenuList dense>
                    <MenuItem
                      onClick={() => onRightSelect(null)}
                      className={right_operand_id === null ? 'lineSelected' : ''}
                    >
                      <ListItemIcon>{right_operand_id === null ? <Check /> : null}</ListItemIcon>
                      <ListItemText inset>
                        <TextField
                          type={textFieldTypes.NUMBER}
                          name="right_operand_value"
                          id="right_operand_value"
                          value={right_operand_value ? right_operand_value : '0'}
                          variant={variantTypes.FILLED}
                          className="common_fullwidth customValue"
                          onChange={onRightOperandValueChange}
                          placeholder={t('flowActions.conditions.dialog.accordion.custom_value.placeholder')}
                        />
                      </ListItemText>
                    </MenuItem>
                  </MenuList>
                </AccordionDetails>
              </Accordion>
              <FormControl error={Boolean(errors && errors?.right_operand)}>
                <FormHelperText>{(errors && errors?.right_operand) || ''}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.cancel')}
            icon={<CloseIcon />}
            onClick={onDialogClose}
          />
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            type={buttonTypes.SUBMIT}
            text={t('common.create')}
            onClick={onSave}
            icon={<SaveOutlinedIcon />}
          />
        </DialogActions>
      </Dialog>
    )
  )
}

FlowElementConditionDialog.displayName = 'FlowElementConditionDialog'

FlowElementConditionDialog.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentCondition: PropTypes.object,
  currentTaskInstance: PropTypes.object,
  variables: PropTypes.arrayOf(PropTypes.object),
  resources: PropTypes.arrayOf(PropTypes.object),
  parameters: PropTypes.arrayOf(PropTypes.object),
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired
  // -- refresh after saving
  // refreshAfterSaving: PropTypes.func
}

FlowElementConditionDialog.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentCondition: null,
  currentTaskInstance: null,
  variables: [],
  resources: [],
  parameters: [],
  openDialog: false,
  setOpenDialog: null
  // -- refresh after saving
  // refreshAfterSaving: () => {}
}
